import { useMemo } from 'react';
import {
  closestCenter,
  DndContext,
  useDroppable,
  DragOverlay,
  useSensors,
  useSensor,
  PointerSensor,
} from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { css } from '@emotion/react';
import { pick } from 'lodash-es';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { getFullName } from '@frontend/name';
import { ScheduleFFHelpers } from '@frontend/schedule-helpers';
import { SettingsRoutes, useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { useSchedulingLocationInfo } from '../../../hooks/use-scheduling-location-info';
import type { CalendarEntityType } from '../../../stores/calendar.store';
import {
  COLUMN_HEADER_HEIGHT,
  getMaxProviderCount,
  MIN_DRAG_DISTANCE,
  PROVIDER_COLUMN_GAP_NEW,
  PROVIDER_COLUMN_GAP,
  PROVIDER_LANE_WIDTH,
} from '../constants';
import { ColumnEntity, LocationWithCalendarColumnEntities } from '../types';
import { ColumnHeader, type ColumnHeaderProps } from './column-header/column-header';
import { useReorderedColumnEntities } from './hooks/use-reordered-column-entities';
import { LocationBar } from './location-bar';

interface LocationListWithColumnEntitiesProps {
  calendarEntityType: CalendarEntityType;
  list: LocationWithCalendarColumnEntities[];
  maxWidth: number;
}

export const LocationListWithColumnEntities = ({
  calendarEntityType,
  list,
  maxWidth,
}: LocationListWithColumnEntitiesProps) => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { isMultiLocation } = useSchedulingLocationInfo();
  const navigate = useSettingsNavigate();

  const calendarBlockUIFFInfo = ScheduleFFHelpers.useCalendarBlockUIFeatureFlag();
  const providerColumnGap = calendarBlockUIFFInfo.isEnabled ? PROVIDER_COLUMN_GAP_NEW : PROVIDER_COLUMN_GAP;

  const { gridTemplateColumns, showLocationBar, totalColumnEntities } = useMemo(() => {
    const totalColEntities = list.reduce((acc, location) => acc + location.columnEntities?.length, 0);
    const gridTemplateCols = list
      .map((item) => (item.columnEntities?.length ? `${item.columnEntities?.length}fr` : ''))
      .join(' ');

    return {
      gridTemplateColumns: gridTemplateCols,
      showLocationBar: list.length > 1 || isMultiLocation,
      totalColumnEntities: totalColEntities,
    };
  }, [list, isMultiLocation]);

  const handleColumnHeaderClick = (locationId: string, entityId: string) => {
    if (!locationId || !entityId) return;

    let route: SettingsRoutes;
    let params;

    if (calendarEntityType === 'provider') {
      route = isMultiLocation
        ? '/schedule/manage-providers/:locationId/edit/:providerId'
        : '/schedule/manage-providers/edit/:providerId';
      params = isMultiLocation ? { locationId, providerId: entityId } : { providerId: entityId };
    } else {
      route = isMultiLocation
        ? '/schedule/operatories/:locationId/edit/:operatoryId'
        : '/schedule/operatories/edit/:operatoryId';
      params = isMultiLocation ? { locationId, operatoryId: entityId } : { operatoryId: entityId };
    }

    navigate.navigate({ to: route, params });
  };

  const shouldSetMaxWidth = getMaxProviderCount(maxWidth) < totalColumnEntities;

  return (
    <section css={styles.mainContainer(gridTemplateColumns, providerColumnGap)}>
      {list.map((location, index) => (
        <div key={index} css={index !== 0 && styles.itemSeparator(providerColumnGap)}>
          {showLocationBar && (
            <LocationBar
              name={location.name}
              timezone={location.timezone}
              maxWidth={maxWidth}
              columnEntityCount={location.columnEntities?.length}
            />
          )}
          <ColumnEntityList
            isScheduleV3FlagEnabled={isScheduleV3FlagEnabled}
            calendarEntityType={calendarEntityType}
            location={location}
            shouldSetMaxWidth={shouldSetMaxWidth}
            handleColumnHeaderClick={handleColumnHeaderClick}
          />
        </div>
      ))}
    </section>
  );
};

type ColumnEntityListProps = {
  isScheduleV3FlagEnabled: boolean;
  calendarEntityType: CalendarEntityType;
  location: LocationWithCalendarColumnEntities;
  shouldSetMaxWidth: boolean;
  handleColumnHeaderClick: (locationId: string, entityId: string) => void;
};

const ColumnEntityList = ({
  isScheduleV3FlagEnabled,
  calendarEntityType,
  location,
  shouldSetMaxWidth,
  handleColumnHeaderClick,
}: ColumnEntityListProps) => {
  const { isEnabled: isColumnEntityReorderEnabled } = ScheduleFFHelpers.useCalendarColumnsReorderingFeatureFlag();
  const {
    originalReorderedList,
    list: reorderedList,
    setList,
    resetList,
    dragDropContextProps,
    droppableProps,
    draggedItem,
  } = useReorderedColumnEntities(location, calendarEntityType);
  const list = isColumnEntityReorderEnabled ? reorderedList : location.columnEntities;

  const { setNodeRef } = useDroppable({
    id: droppableProps.id,
  });
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: MIN_DRAG_DISTANCE,
      },
    })
  );
  const commonHeaderProps = {
    shouldSetMaxWidth,
    originalReorderedList,
    list: reorderedList,
    setList,
    resetList,
    locationId: location.id,
    calendarEntityType,
    isColumnEntityReorderEnabled,
  };
  const dragOverlayProps = getColumnHeaderProps(draggedItem, {
    ...commonHeaderProps,
    index: -1,
    isOverlay: true,
  });

  return (
    <DndContext
      {...dragDropContextProps}
      sensors={sensors}
      collisionDetection={closestCenter}
      autoScroll={{
        threshold: {
          x: 0.2, // Default or desired value for horizontal auto-scroll
          y: 0, // Set to 0 to block vertical auto-scroll
        },
      }}
    >
      <SortableContext id={location.id} items={list} strategy={horizontalListSortingStrategy}>
        <div css={styles.listContainer} ref={setNodeRef}>
          {list.map((columnEntity, index) => {
            const props = getColumnHeaderProps(columnEntity, {
              ...commonHeaderProps,
              index,
              onClick: isScheduleV3FlagEnabled
                ? () => handleColumnHeaderClick(location.id, columnEntity.id)
                : undefined,
            });
            if (!props) {
              return null;
            }
            return <ColumnHeader key={columnEntity.id} {...props} />;
          })}
        </div>
      </SortableContext>
      <DragOverlay>{dragOverlayProps && <ColumnHeader {...dragOverlayProps} />}</DragOverlay>
    </DndContext>
  );
};

const styles = {
  mainContainer: (gridTemplateColumns: string, providerColumnGap: number) =>
    css({
      display: 'grid',
      gridTemplateColumns: gridTemplateColumns,
      gridTemplateRows: 'auto',
      width: '100%',
      gap: `${providerColumnGap}px`,
      marginRight: theme.spacing(2),
    }),
  itemSeparator: (providerColumnGap: number) =>
    css({
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        width: 1,
        height: '100%',
        backgroundColor: theme.colors.neutral10,
        top: 0,
        left: `-${providerColumnGap / 2}px`, // center of the space between location groups
      },
    }),
  listContainer: css({
    display: 'flex',
    height: COLUMN_HEADER_HEIGHT,
    '> *': {
      flex: `1 0 ${PROVIDER_LANE_WIDTH}px`,
      justifyContent: 'center',
      '&:not(:last-child)': {
        boxShadow: `1px 0 0 ${theme.colors.neutral10}`,
      },
    },
  }),
};

const getColumnHeaderProps = (
  columnEntity: ColumnEntity | undefined,
  otherProps: {
    shouldSetMaxWidth: boolean;
    index: number;
    originalReorderedList: ColumnEntity[];
    list: ColumnEntity[];
    setList: React.Dispatch<React.SetStateAction<ColumnEntity[]>>;
    locationId: string;
    calendarEntityType: CalendarEntityType;
    isColumnEntityReorderEnabled: boolean;
    resetList: () => void;
    onClick?: () => void;
    isOverlay?: boolean;
  }
): ColumnHeaderProps | undefined => {
  if (!columnEntity) {
    return undefined;
  }
  return {
    name: getFullName(columnEntity),
    avatarVariant: otherProps.calendarEntityType === 'provider' ? 'circle' : 'square',
    ...pick(columnEntity, [
      'id',
      'firstName',
      'lastName',
      'avatarSrc',
      'iconName',
      'iconColor',
      'isArchived',
      'eventCount',
    ]),
    ...otherProps,
  };
};
